// UNIT affiliations: self-selected series a member identifies with (catalogue ids, never a
// hard-coded list). Used by first-profile onboarding, the Edit profile sheet and the profile.
// Selecting one grants nothing: no team, role or permission.
import { useId } from 'react';
import { C, R, font } from '../theme/tokens';
import { photoUrl } from '../data/photos';
import type { SeriesDef } from '../domain/series';
import { CheckIcon, PlusIcon } from './icons';

/** Toggle `id` in `list`; the primary is kept, or becomes the first remaining one. */
export function toggleAffiliation(list: readonly string[], primary: string, id: string): { list: string[]; primary: string } {
  const next = list.includes(id) ? list.filter((s) => s !== id) : [...list, id];
  return { list: next, primary: next.includes(primary) ? primary : (next[0] ?? '') };
}

/** Small round badge: the series art when the catalogue has it, else its initial. */
export function UnitBadge({ series, size = 28, ring = C.white }: { series?: SeriesDef; size?: number; ring?: string }) {
  const art = series?.art;
  return (
    <span aria-hidden="true" style={{ width: size, height: size, flex: 'none', borderRadius: '50%', overflow: 'hidden', border: `2px solid ${ring}`, background: C.soft, color: C.violet, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', font: font(700, Math.round(size * 0.42), 1) }}>
      {art ? <img src={photoUrl(art)} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (series?.id ?? '?').slice(0, 1)}
    </span>
  );
}

/** Multi-select chips (aria-pressed). `items` are the chips to show; `selected` marks the pressed ones. */
export function AffiliationChips({ items, selected, primary, onToggle, label }: { items: readonly SeriesDef[]; selected: readonly string[]; primary?: string; onToggle: (id: string) => void; label: string }) {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {items.map((s) => {
        const on = selected.includes(s.id);
        const main = on && primary === s.id && selected.length > 1;
        return (
          <button key={s.id} type="button" aria-pressed={on} onClick={() => onToggle(s.id)} className="press"
            aria-label={`${s.label}${main ? ', primary' : ''}`}
            style={{ height: 44, padding: on ? '0 6px 0 4px' : '0 14px 0 4px', borderRadius: R.pill, border: `1px solid ${on ? C.violet : C.border}`, background: on ? C.soft : C.white, color: C.ink, font: font(600, 13), display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap', transition: 'background .2s, border-color .2s' }}>
            <UnitBadge series={s} size={34} ring={on ? C.soft : C.white} />
            <span style={{ letterSpacing: '.02em' }}>{s.id.toUpperCase()}</span>
            {on
              ? <span aria-hidden="true" style={{ width: 24, height: 24, borderRadius: '50%', background: C.violet, color: C.white, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><CheckIcon size={14} /></span>
              : <span aria-hidden="true" style={{ color: C.grey, display: 'inline-flex' }}><PlusIcon size={14} /></span>}
          </button>
        );
      })}
    </div>
  );
}

/** Choose the primary among the selected (only shown with two or more). Native select: compact and accessible. */
export function PrimarySelect({ catalogue, selected, primary, onChange }: { catalogue: readonly SeriesDef[]; selected: readonly string[]; primary: string; onChange: (id: string) => void }) {
  const id = useId();
  if (selected.length < 2) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <label htmlFor={id} style={{ font: font(500, 13), color: C.grey, flex: 'none' }}>Primary</label>
      <select id={id} value={primary} onChange={(e) => onChange(e.target.value)}
        style={{ flex: 1, minWidth: 0, height: 44, borderRadius: R.input, border: `1px solid ${C.border}`, background: C.white, color: C.ink, font: font(600, 16), padding: '0 10px' }}>
        {selected.map((s) => <option key={s} value={s}>{catalogue.find((c) => c.id === s)?.label ?? `${s} UNITS`}</option>)}
      </select>
    </div>
  );
}
